import { useState } from "react";
import {
  pizzasTomate,
  pizzasCreme,
  burgers,
  menuEnfant,
  supplements,
} from "../data/menu";
import MenuCard from "./MenuCard";

function formatEUR(v) {
  return v.toFixed(2).replace(".", ",") + " €";
}

const TABS = [
  { id: "tomate", label: "Base Tomate", count: pizzasTomate.length },
  { id: "creme", label: "Base Crème", count: pizzasCreme.length },
  { id: "burgers", label: "Burgers", count: burgers.length },
  { id: "extras", label: "Enfant & Suppléments", count: null },
];

// Toutes les pizzas de la maison — données extraites des deux PDFs
// (Cartes Image_20260517_0001.pdf et PICOLA20260516_0010.pdf).

export default function MenuSection() {
  const [tab, setTab] = useState("tomate");

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-[#fdfbf7] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.32em] text-[#c23b22] mb-4">
            <i className="fa-solid fa-utensils mr-2"></i>
            Notre carte
          </p>
          <h2
            data-testid="menu-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a1a1a]"
          >
            Nos pizzas, <em className="italic text-[#2d4a22]">faites à la main.</em>
          </h2>
          <p className="mt-6 text-[#5c5c5c] text-base sm:text-lg leading-relaxed">
            Toutes nos pizzas sont disponibles en deux tailles : <strong>Moyenne</strong> (Ø 29 cm) et <strong>Grande</strong> (Ø 33 cm). Pâte pétrie chaque jour dans notre cuisine, préparée et cuite à la commande.
          </p>
        </div>

        {/* Tabs */}
        <div
          className="mt-12 flex flex-wrap gap-2 sm:gap-3 border-b border-[#e5e0d5]"
          data-testid="menu-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`menu-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative px-5 py-3 text-xs sm:text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                tab === t.id
                  ? "text-[#c23b22]"
                  : "text-[#5c5c5c] hover:text-[#1a1a1a]"
              }`}
            >
              {t.label}
              {t.count != null && (
                <span className="ml-1 text-[10px] opacity-60">({t.count})</span>
              )}
              {tab === t.id && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#c23b22]"></span>
              )}
            </button>
          ))}
        </div>

        {/* Contenu */}
        <div className="mt-10">
          {tab === "tomate" && (
            <div
              data-testid="menu-grid-tomate"
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7"
            >
              {pizzasTomate.map((p) => (
                <MenuCard key={p.slug} pizza={p} />
              ))}
            </div>
          )}

          {tab === "creme" && (
            <div
              data-testid="menu-grid-creme"
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7"
            >
              {pizzasCreme.map((p) => (
                <MenuCard key={p.slug} pizza={p} />
              ))}
            </div>
          )}

          {tab === "burgers" && (
            <div
              data-testid="menu-grid-burgers"
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7"
            >
              {burgers.map((b, i) => (
                <article
                  key={i}
                  data-testid={`burger-card-${i}`}
                  className="menu-card bg-white border border-[#e5e0d5] rounded-2xl p-5 sm:p-6 flex gap-5 sm:gap-7 items-center relative overflow-hidden"
                >
                  <div className="relative shrink-0">
                    <div className="absolute inset-0 rounded-full bg-[#c23b22]/10 blur-xl"></div>
                    <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden ring-4 ring-white shadow-[0_8px_22px_-12px_rgba(0,0,0,0.35)] bg-[#f6efe2]">
                      <img
                        src={b.img}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        alt={b.name}
                        loading="lazy"
                        className="menu-img w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-serif text-xl sm:text-2xl text-[#1a1a1a] leading-tight">
                      {b.name}
                    </h3>
                    <p className="mt-2 text-sm sm:text-[15px] text-[#5c5c5c] leading-relaxed">
                      {b.ingredients}
                    </p>
                    <p className="mt-4 font-serif text-base sm:text-lg text-[#c23b22] font-semibold">
                      {formatEUR(b.price)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}

          {tab === "extras" && (
            <div
              data-testid="menu-grid-extras"
              className="grid grid-cols-1 lg:grid-cols-2 gap-7"
            >
              {/* Menu Enfant */}
              <div className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d5] flex flex-col">
                <div className="relative h-64 sm:h-72 shrink-0">
                  <img
                    src="/pizzas/menu-enfant.webp"
                    alt="Menu Enfant"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <p className="text-xs uppercase tracking-[0.24em] text-[#c23b22] mb-3">
                    Pour les enfants
                  </p>
                  <h3 className="font-serif text-3xl text-[#1a1a1a]">{menuEnfant.name}</h3>
                  <p className="mt-3 text-[#5c5c5c] leading-relaxed">
                    {menuEnfant.ingredients}
                  </p>
                  <p className="mt-6 font-serif text-3xl text-[#c23b22] font-semibold">
                    {formatEUR(menuEnfant.price)}
                  </p>
                </div>
              </div>

              {/* Suppléments */}
              <div className="bg-white border border-[#e5e0d5] rounded-2xl p-8">
                <p className="text-xs uppercase tracking-[0.24em] text-[#c23b22] mb-3">
                  Compléter votre pizza
                </p>
                <h3 className="font-serif text-3xl text-[#1a1a1a]">
                  Suppléments
                </h3>
                <ul className="mt-6 divide-y divide-[#e5e0d5]">
                  {supplements.map((s, i) => (
                    <li
                      key={i}
                      data-testid={`supplement-${i}`}
                      className="flex items-center justify-between py-3 text-sm"
                    >
                      <span className="text-[#1a1a1a]">{s.label}</span>
                      <span className="font-serif text-base text-[#c23b22] font-semibold">
                        {formatEUR(s.price)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Note finale */}
        <p
          data-testid="menu-disclaimer"
          className="mt-12 text-xs text-[#5c5c5c] italic"
        >
          Photos non contractuelles. Pour toute information sur les allergènes,
          merci de nous contacter au {`09 81 64 85 95`}.
        </p>
      </div>
    </section>
  );
}
